// src/components/AssistiveTouchBubble.jsx
// Modern floating bubble for drivers to quickly return to active trip
// Follows project design system: grays, emerald, blue, purple, amber
// Includes Uber-like sound effects

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchBookings } from "../actions/BookingActions";
import { 
  Navigation, 
  MapPin, 
  Car, 
  Flag, 
  X, 
  ChevronRight,
  Truck,
  Circle,
  Clock,
  Play,
  ArrowRight,
  Target,
  Compass,
  Volume2,
  VolumeX
} from "lucide-react";

const AssistiveTouchBubble = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => state.auth);

  // Active booking state
  const [activeBooking, setActiveBooking] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [bookingProgress, setBookingProgress] = useState(0);

  // Dragging state
  const [position, setPosition] = useState({ x: 20, y: window.innerHeight - 150 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const bubbleRef = useRef(null);

  // UI state
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPulsing, setIsPulsing] = useState(true);
  const [showReminder, setShowReminder] = useState(true);
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const [hasPlayedNotification, setHasPlayedNotification] = useState(false);
  const reminderTimeoutRef = useRef(null);
  const audioContextRef = useRef(null);

  // Check if user is a driver
  const isDriver = userDetails?.role?.toLowerCase() === "driver" || userDetails?.role === "D";
  const driverSno = userDetails?.ServiceNo || userDetails?.serviceNo;

  // Initialize Audio Context
  useEffect(() => {
    if (typeof window !== 'undefined') {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Uber-like sound effects
  const playSound = useCallback((type) => {
    if (!isSoundEnabled || !audioContextRef.current) return;
    
    const audioCtx = audioContextRef.current;
    const now = audioCtx.currentTime;
    
    // Resume AudioContext if suspended (browser policy)
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }
    
    switch(type) {
      case 'bubbleAppear':
        // Gentle "pop" sound like Uber's bubble appearing
        const popOsc = audioCtx.createOscillator();
        const popGain = audioCtx.createGain();
        popOsc.connect(popGain);
        popGain.connect(audioCtx.destination);
        popOsc.frequency.value = 880;
        popGain.gain.value = 0.15;
        popOsc.type = 'sine';
        popGain.gain.exponentialRampToValueAtTime(0.00001, now + 0.3);
        popOsc.start();
        popOsc.stop(now + 0.2);
        break;
        
      case 'tap':
        // Soft "click" sound for interactions
        const tapOsc = audioCtx.createOscillator();
        const tapGain = audioCtx.createGain();
        tapOsc.connect(tapGain);
        tapGain.connect(audioCtx.destination);
        tapOsc.frequency.value = 440;
        tapGain.gain.value = 0.1;
        tapOsc.type = 'sine';
        tapGain.gain.exponentialRampToValueAtTime(0.00001, now + 0.15);
        tapOsc.start();
        tapOsc.stop(now + 0.1);
        break;
        
      case 'reminder':
        // Uber-like notification "ding"
        const dingOsc = audioCtx.createOscillator();
        const dingGain = audioCtx.createGain();
        dingOsc.connect(dingGain);
        dingGain.connect(audioCtx.destination);
        dingOsc.frequency.value = 1046.5; // C6 note
        dingGain.gain.value = 0.12;
        dingOsc.type = 'sine';
        dingGain.gain.exponentialRampToValueAtTime(0.00001, now + 0.4);
        dingOsc.start();
        dingOsc.stop(now + 0.3);
        
        // Second harmonic
        const dingOsc2 = audioCtx.createOscillator();
        const dingGain2 = audioCtx.createGain();
        dingOsc2.connect(dingGain2);
        dingGain2.connect(audioCtx.destination);
        dingOsc2.frequency.value = 1318.52; // E6 note
        dingGain2.gain.value = 0.08;
        dingOsc2.type = 'sine';
        dingGain2.gain.exponentialRampToValueAtTime(0.00001, now + 0.35);
        dingOsc2.start();
        dingOsc2.stop(now + 0.25);
        break;
        
      case 'navigate':
        // "Whoosh" sound for navigation start
        const whooshGain = audioCtx.createGain();
        const filter = audioCtx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 800;
        whooshGain.connect(filter);
        filter.connect(audioCtx.destination);
        
        const noise = audioCtx.createBufferSource();
        const bufferSize = audioCtx.sampleRate * 0.3;
        const buffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
          data[i] = Math.random() * 2 - 1;
        }
        noise.buffer = buffer;
        noise.connect(whooshGain);
        whooshGain.gain.value = 0.08;
        whooshGain.gain.exponentialRampToValueAtTime(0.00001, now + 0.25);
        noise.start();
        noise.stop(now + 0.25);
        break;
        
      case 'emergency':
        // Alert sound for emergency
        const alertOsc = audioCtx.createOscillator();
        const alertGain = audioCtx.createGain();
        alertOsc.connect(alertGain);
        alertGain.connect(audioCtx.destination);
        alertOsc.frequency.value = 880;
        alertGain.gain.value = 0.2;
        alertOsc.type = 'sawtooth';
        
        // Tremolo effect
        const lfo = audioCtx.createOscillator();
        const lfoGain = audioCtx.createGain();
        lfo.connect(lfoGain);
        lfoGain.connect(alertGain.gain);
        lfo.frequency.value = 8;
        lfoGain.gain.value = 0.15;
        
        lfo.start();
        alertOsc.start();
        alertGain.gain.exponentialRampToValueAtTime(0.00001, now + 1);
        alertOsc.stop(now + 1);
        lfo.stop(now + 1);
        break;
        
      default:
        break;
    }
  }, [isSoundEnabled]);

  // Play appearance sound when bubble becomes visible
  useEffect(() => {
    if (isVisible && !hasPlayedNotification) {
      setTimeout(() => {
        playSound('bubbleAppear');
        setHasPlayedNotification(true);
      }, 500);
    }
  }, [isVisible, playSound, hasPlayedNotification]);

  // Get center icon based on status
  const getCenterIcon = (status) => {
    const normalizedStatus = status?.toUpperCase().trim() || "";
    
    if (normalizedStatus === "IP" || normalizedStatus === "INPROGRESS" || normalizedStatus === "IN PROGRESS") {
      return { 
        Icon: Play, 
        label: "Trip in Progress",
        animation: "animate-pulse"
      };
    } else if (normalizedStatus === "EN" || normalizedStatus === "ENROUTE" || normalizedStatus === "E") {
      return { 
        Icon: Compass, 
        label: "En Route",
        animation: "animate-spin-slow"
      };
    } else if (normalizedStatus === "A" || normalizedStatus === "ACTIVE") {
      return { 
        Icon: Target, 
        label: "Active",
        animation: "animate-bounce-subtle"
      };
    } else {
      return { 
        Icon: Car, 
        label: "Trip",
        animation: ""
      };
    }
  };

  // Get status configuration based on project's color system
  const getStatusConfig = (status) => {
    const normalizedStatus = status?.toUpperCase().trim() || "";
    
    if (normalizedStatus === "IP" || normalizedStatus === "INPROGRESS" || normalizedStatus === "IN PROGRESS") {
      return {
        label: "In Progress",
        shortLabel: "Trip",
        icon: Car,
        iconColor: "text-purple-600",
        bgGradient: "from-purple-500 to-purple-600",
        badgeColor: "bg-purple-100 text-purple-700",
        ringColor: "ring-purple-200",
        centerBg: "bg-white/20"
      };
    } else if (normalizedStatus === "EN" || normalizedStatus === "ENROUTE" || normalizedStatus === "E") {
      return {
        label: "En Route",
        shortLabel: "En Route",
        icon: Navigation,
        iconColor: "text-blue-600",
        bgGradient: "from-blue-500 to-blue-600",
        badgeColor: "bg-blue-100 text-blue-700",
        ringColor: "ring-blue-200",
        centerBg: "bg-white/20"
      };
    } else if (normalizedStatus === "A" || normalizedStatus === "ACTIVE") {
      return {
        label: "Active",
        shortLabel: "Active",
        icon: Circle,
        iconColor: "text-emerald-600",
        bgGradient: "from-emerald-500 to-emerald-600",
        badgeColor: "bg-emerald-100 text-emerald-700",
        ringColor: "ring-emerald-200",
        centerBg: "bg-white/20"
      };
    } else {
      return {
        label: "Trip Active",
        shortLabel: "Trip",
        icon: Truck,
        iconColor: "text-gray-600",
        bgGradient: "from-gray-600 to-gray-700",
        badgeColor: "bg-gray-100 text-gray-700",
        ringColor: "ring-gray-200",
        centerBg: "bg-white/15"
      };
    }
  };

  // Fetch active bookings for this driver
  const fetchActiveBooking = useCallback(async () => {
    if (!isDriver || !driverSno) {
      setIsVisible(false);
      return;
    }

    try {
      const allBookings = await dispatch(fetchBookings());
      const activeStatuses = ["A", "ACTIVE", "EN", "ENROUTE", "E", "IP", "INPROGRESS", "IN PROGRESS"];
      
      const driverActiveBooking = allBookings.find((booking) => {
        const status = booking.status?.toUpperCase().trim() || "";
        return booking.dSNo === driverSno && activeStatuses.includes(status);
      });

      if (driverActiveBooking) {
        const wasInvisible = !isVisible;
        setActiveBooking(driverActiveBooking);
        setIsVisible(true);
        localStorage.setItem("driver_active_booking", JSON.stringify(driverActiveBooking));
        
        // Calculate progress based on status
        const status = driverActiveBooking.status?.toUpperCase().trim() || "";
        if (status === "IP" || status === "INPROGRESS") {
          setBookingProgress(75);
        } else if (status === "EN" || status === "ENROUTE") {
          setBookingProgress(50);
        } else {
          setBookingProgress(25);
        }
        
        // Play reminder sound when booking appears
        if (wasInvisible && showReminder && !location.pathname.includes("/driver-trip")) {
          playSound('reminder');
        }
      } else {
        setActiveBooking(null);
        setIsVisible(false);
        localStorage.removeItem("driver_active_booking");
        setBookingProgress(0);
        setHasPlayedNotification(false);
      }
    } catch (err) {
      console.error("[AssistiveTouchBubble] Failed to fetch bookings:", err);
      const cached = localStorage.getItem("driver_active_booking");
      if (cached) {
        try {
          const cachedBooking = JSON.parse(cached);
          setActiveBooking(cachedBooking);
          setIsVisible(true);
        } catch {
          setIsVisible(false);
        }
      }
    }
  }, [dispatch, isDriver, driverSno, isVisible, location.pathname, showReminder, playSound]);

  // Load saved position from localStorage
  useEffect(() => {
    const savedPosition = localStorage.getItem("bubble_position");
    if (savedPosition) {
      try {
        const pos = JSON.parse(savedPosition);
        const maxX = window.innerWidth - 70;
        const maxY = window.innerHeight - 70;
        setPosition({
          x: Math.min(Math.max(0, pos.x), maxX),
          y: Math.min(Math.max(0, pos.y), maxY),
        });
      } catch {
        // Use default position
      }
    }
  }, []);

  // Fetch active booking on mount and periodically
  useEffect(() => {
    if (!isDriver) return;
    fetchActiveBooking();
    const interval = setInterval(fetchActiveBooking, 10000);
    return () => clearInterval(interval);
  }, [isDriver, fetchActiveBooking]);

  // Re-check when route changes
  useEffect(() => {
    if (isDriver) {
      fetchActiveBooking();
    }
  }, [location.pathname, isDriver, fetchActiveBooking]);

  // Auto-hide reminder after 10 seconds
  useEffect(() => {
    if (showReminder && !location.pathname.includes("/driver-trip")) {
      reminderTimeoutRef.current = setTimeout(() => {
        setShowReminder(false);
      }, 10000);
    }
    return () => {
      if (reminderTimeoutRef.current) {
        clearTimeout(reminderTimeoutRef.current);
      }
    };
  }, [showReminder, location.pathname]);

  // Handle drag start
  const handleDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const clientX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;

    setIsDragging(true);
    setDragOffset({
      x: clientX - position.x,
      y: clientY - position.y,
    });
    setIsPulsing(false);
    playSound('tap');
  };

  // Handle drag move
  const handleDragMove = useCallback(
    (e) => {
      if (!isDragging) return;
      e.preventDefault();

      const clientX = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
      const clientY = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;

      const newX = clientX - dragOffset.x;
      const newY = clientY - dragOffset.y;

      const maxX = window.innerWidth - 70;
      const maxY = window.innerHeight - 70;

      setPosition({
        x: Math.min(Math.max(0, newX), maxX),
        y: Math.min(Math.max(0, newY), maxY),
      });
    },
    [isDragging, dragOffset]
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setIsPulsing(true);
      
      // Snap to edge
      const midX = window.innerWidth / 2;
      const snapX = position.x < midX ? 10 : window.innerWidth - 80;
      const newPosition = { ...position, x: snapX };
      setPosition(newPosition);
      localStorage.setItem("bubble_position", JSON.stringify(newPosition));
    }
  }, [isDragging, position]);

  // Add global event listeners
  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleDragMove);
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchmove", handleDragMove, { passive: false });
      window.addEventListener("touchend", handleDragEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleDragMove);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Handle bubble click
  const handleBubbleClick = () => {
    if (isDragging) return;
    if (activeBooking?.bId) {
      playSound('tap');
      navigate(`/driver-trip/${activeBooking.bId}`);
    }
  };

  // Handle return to trip
  const handleReturnToTrip = () => {
    playSound('navigate');
    if (activeBooking?.bId) {
      navigate(`/driver-trip/${activeBooking.bId}`);
    }
  };

  // Dismiss reminder
  const handleDismissReminder = (e) => {
    e.stopPropagation();
    playSound('tap');
    setShowReminder(false);
  };

  // Toggle sound
  const toggleSound = (e) => {
    e.stopPropagation();
    playSound('tap');
    setIsSoundEnabled(!isSoundEnabled);
  };

  // Don't render if not driver or no active booking
  if (!isDriver || !isVisible || !activeBooking) {
    return null;
  }

  const statusConfig = getStatusConfig(activeBooking.status);
  const { Icon: CenterIcon, animation: centerAnimation } = getCenterIcon(activeBooking.status);
  const StatusIcon = statusConfig.icon;

  return (
    <>
      {/* Floating Bubble */}
      <div
        ref={bubbleRef}
        className={`fixed z-[9999] select-none touch-none ${
          isDragging ? "cursor-grabbing" : "cursor-grab"
        }`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          transition: isDragging ? "none" : "left 0.2s ease-out, top 0.2s ease-out",
        }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        onClick={handleBubbleClick}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {/* Main Bubble */}
        <div
          className={`
            relative flex items-center justify-center
            w-14 h-14 sm:w-16 sm:h-16
            rounded-full shadow-lg
            bg-gradient-to-br ${statusConfig.bgGradient}
            border-2 border-white
            transition-all duration-300 ease-out
            hover:shadow-xl
            ${isPulsing ? "animate-pulse" : ""}
            ${isExpanded ? "scale-110" : "scale-100"}
          `}
        >
          {/* Pulse ring effect */}
          <div
            className={`
              absolute inset-0 rounded-full
              bg-gradient-to-br ${statusConfig.bgGradient}
              animate-ping opacity-30
            `}
          />
          
          {/* Progress ring */}
          <svg className="absolute inset-0 w-full h-full -rotate-90">
            <circle
              cx="50%"
              cy="50%"
              r="30"
              fill="none"
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="3"
            />
            <circle
              cx="50%"
              cy="50%"
              r="30"
              fill="none"
              stroke="white"
              strokeWidth="3"
              strokeDasharray={`${2 * Math.PI * 30}`}
              strokeDashoffset={`${2 * Math.PI * 30 * (1 - bookingProgress / 100)}`}
              className="transition-all duration-500"
            />
          </svg>

          {/* Center Circle with Icon */}
          <div className={`
            absolute inset-0 flex items-center justify-center
            w-10 h-10 sm:w-12 sm:h-12
            rounded-full ${statusConfig.centerBg}
            backdrop-blur-[1px]
            transition-all duration-300
            ${isExpanded ? "scale-110" : "scale-100"}
          `}>
            <div className={centerAnimation}>
              <CenterIcon 
                size={20} 
                className="text-white sm:hidden drop-shadow-md" 
                strokeWidth={1.8}
              />
              <CenterIcon 
                size={24} 
                className="text-white hidden sm:block drop-shadow-md" 
                strokeWidth={1.8}
              />
            </div>
          </div>

          {/* Notification badge */}
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
            <span className="text-white text-[10px] font-bold">1</span>
          </div>
        </div>

        {/* Sound toggle indicator */}
        <div 
          onClick={toggleSound}
          className="absolute -bottom-6 left-1/2 -translate-x-1/2 cursor-pointer"
        >
          {isSoundEnabled ? (
            <Volume2 size={12} className="text-gray-400 hover:text-gray-600 transition" />
          ) : (
            <VolumeX size={12} className="text-gray-400 hover:text-gray-600 transition" />
          )}
        </div>

        {/* Expanded Tooltip - Desktop only */}
        <div
          className={`
            absolute top-1/2 -translate-y-1/2 hidden sm:block
            ${position.x < window.innerWidth / 2 ? "left-20" : "right-20"}
            bg-gray-900 text-white px-4 py-2.5 rounded-lg shadow-lg
            whitespace-nowrap transition-all duration-300
            ${isExpanded ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"}
          `}
        >
          <div className="flex items-center gap-2">
            <CenterIcon size={14} className="text-gray-300" />
            <span className="text-sm font-medium">{statusConfig.label} Trip</span>
            <span className="text-gray-500 text-xs">•</span>
            <span className="text-gray-300 text-xs">Booking #{activeBooking.bId}</span>
            <ChevronRight size={12} className="text-gray-400 ml-1" />
          </div>
          {/* Arrow pointer */}
          <div
            className={`
              absolute top-1/2 -translate-y-1/2 w-0 h-0
              border-t-6 border-b-6 border-transparent
              ${position.x < window.innerWidth / 2 
                ? "-left-2 border-r-8 border-r-gray-900" 
                : "-right-2 border-l-8 border-l-gray-900"
              }
            `}
          />
        </div>

        {/* Mobile expanded label - shows on tap */}
        <div
          className={`
            absolute top-1/2 -translate-y-1/2 sm:hidden
            ${position.x < window.innerWidth / 2 ? "left-16" : "right-16"}
            bg-gray-900 text-white px-3 py-1.5 rounded-lg shadow-lg
            whitespace-nowrap transition-all duration-300
            ${isExpanded ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"}
          `}
        >
          <div className="flex items-center gap-1.5">
            <CenterIcon size={10} className="text-gray-300" />
            <span className="text-xs font-medium">{statusConfig.shortLabel}</span>
            <span className="text-gray-400 text-[10px]">#{activeBooking.bId}</span>
          </div>
        </div>
      </div>

      {/* Reminder Banner - Uber Style with Sound */}
      {!location.pathname.includes("/driver-trip") && showReminder && (
        <div
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-20 sm:max-w-sm z-[9998] 
                     bg-white border border-gray-100 rounded-lg shadow-lg
                     animate-in slide-in-from-bottom-4 duration-300"
          style={{ animationDuration: "0.3s" }}
        >
          <div className="p-3 sm:p-4">
            <div className="flex items-start gap-3">
              {/* Animated Icon with sound wave effect */}
              <div className={`p-2 rounded-lg ${statusConfig.badgeColor} relative`}>
                <CenterIcon size={16} className={statusConfig.iconColor} />
                {isSoundEnabled && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-ping" />
                )}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Active Trip in Progress
                  </h4>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${statusConfig.badgeColor}`}>
                    {statusConfig.label}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Booking #{activeBooking.bId} • Tap the floating bubble to continue
                </p>
                
                {/* Progress bar */}
                <div className="mt-2">
                  <div className="flex justify-between text-[10px] text-gray-400 mb-0.5">
                    <span>Pickup</span>
                    <span>Dropoff</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1">
                    <div 
                      className={`h-1 rounded-full transition-all duration-500 ${
                        bookingProgress >= 75 ? "bg-purple-500" :
                        bookingProgress >= 50 ? "bg-blue-500" :
                        "bg-emerald-500"
                      }`}
                      style={{ width: `${bookingProgress}%` }}
                    />
                  </div>
                </div>
              </div>
              
              {/* Action buttons */}
              <div className="flex flex-col gap-1">
                <button
                  onClick={toggleSound}
                  className="p-1 hover:bg-gray-50 rounded transition flex-shrink-0"
                  title={isSoundEnabled ? "Mute sounds" : "Enable sounds"}
                >
                  {isSoundEnabled ? (
                    <Volume2 size={14} className="text-gray-400" />
                  ) : (
                    <VolumeX size={14} className="text-gray-400" />
                  )}
                </button>
                <button
                  onClick={handleDismissReminder}
                  className="p-1 hover:bg-gray-50 rounded transition flex-shrink-0"
                >
                  <X size={14} className="text-gray-400" />
                </button>
              </div>
            </div>
            
            {/* Action button */}
            <button
              onClick={handleReturnToTrip}
              className="w-full mt-3 px-3 py-2 bg-gray-900 hover:bg-gray-800 
                         text-white text-xs sm:text-sm font-medium rounded-lg
                         transition flex items-center justify-center gap-2
                         active:scale-[0.98]"
            >
              <CenterIcon size={14} />
              <span>Return to Trip</span>
              <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Add custom animation styles */}
      <style jsx>{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes bounce-subtle {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-2px);
          }
        }
        
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        
        .animate-bounce-subtle {
          animation: bounce-subtle 1s ease-in-out infinite;
        }
        
        @keyframes slide-in-from-bottom {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .animate-in {
          animation: slide-in-from-bottom 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default AssistiveTouchBubble;